// All mime types are okay to use from the public API.

import {
  isConversationIncludableFileContentType,
  isQueryableContentType,
  isSearchableContentType,
} from "@app/lib/api/assistant/conversation/content_types";
import logger from "@app/logger/logger";
import type { ContentFragmentInputWithContentNode } from "@app/types/api/assistant";
import type {
  AttachmentCreator,
  BaseConversationAttachmentType,
  ContentNodeAttachmentType,
  ConversationAttachmentType,
  FileAttachmentType,
  LargePasteType,
} from "@app/types/api/assistant/conversation/attachments";
import type {
  ContentFragmentType,
  ContentNodeContentFragmentType,
  FileContentFragmentType,
  SupportedContentFragmentType,
} from "@app/types/content_fragment";
import {
  isContentNodeContentFragment,
  isExpiredContentFragment,
  isFileContentFragment,
} from "@app/types/content_fragment";
import { DATA_SOURCE_NODE_ID } from "@app/types/core/content_node";
import type { AllSupportedFileContentType } from "@app/types/files";
import { isSupportedDelimitedTextContentType } from "@app/types/files";
import { assertNever } from "@app/types/shared/utils/assert_never";
// biome-ignore lint/plugin/enforceClientTypesInPublicApi: existing usage
import { CONTENT_NODE_MIME_TYPES } from "@dust-tt/client";

export function isFileAttachmentType(
  attachment: ConversationAttachmentType
): attachment is FileAttachmentType {
  return "fileId" in attachment;
}

export function isContentNodeAttachmentType(
  attachment: ConversationAttachmentType
): attachment is ContentNodeAttachmentType {
  return "contentFragmentId" in attachment;
}

export function isContentFragmentDataSourceNode(
  attachment: ContentNodeAttachmentType | ContentFragmentInputWithContentNode
): attachment is ContentNodeAttachmentType & {
  nodeId: typeof DATA_SOURCE_NODE_ID;
} {
  return attachment.nodeId === DATA_SOURCE_NODE_ID;
}

// If updating this function, make sure to update `contentFragmentId` when we render the conversation
// for the model. So there is a consistent way to reference content fragments across different actions.
export function conversationAttachmentId(
  attachment: ConversationAttachmentType
): string {
  if (isFileAttachmentType(attachment)) {
    return attachment.fileId;
  }
  return attachment.contentFragmentId;
}

export function getAttachmentFromContentFragment(
  cf: ContentFragmentType
): ConversationAttachmentType | null {
  // Expired content fragments cannot be converted to attachments
  if (isExpiredContentFragment(cf)) {
    return null;
  }

  if (isContentNodeContentFragment(cf)) {
    return getAttachmentFromContentNodeContentFragment(cf);
  }
  if (isFileContentFragment(cf)) {
    return getAttachmentFromFileContentFragment(cf);
  }
  assertNever(cf);
}

export function getAttachmentFromContentNodeContentFragment(
  cf: ContentNodeContentFragmentType & { expiredReason: null }
): ContentNodeAttachmentType {
  const isQueryable =
    isQueryableContentType(cf.contentType) || cf.nodeType === "table";
  const isIncludable =
    cf.nodeType !== "folder" &&
    isConversationIncludableFileContentType(cf.contentType) &&
    // Tables from knowledge are not materialized as raw content. As such, they cannot be
    // included.
    !isQueryable;
  // Tables from knowledge are not materialized as raw content. As such, they cannot be
  // searched--except for notion databases, that may have children.
  const isUnmaterializedTable =
    isQueryable && cf.contentType !== CONTENT_NODE_MIME_TYPES.NOTION.DATABASE;
  const isSearchable =
    isSearchableContentType(cf.contentType) && !isUnmaterializedTable;

  const creator: AttachmentCreator | null = cf.context.fullName
    ? {
        type: "user",
        name: cf.context.fullName,
        pictureUrl: cf.context.profilePictureUrl ?? "",
      }
    : null;

  const baseAttachment: BaseConversationAttachmentType = {
    title: cf.title,
    contentType: cf.contentType,
    snippet: null,
    contentFragmentVersion: cf.contentFragmentVersion,
    // Backward compatibility: we fallback to the fileId if no generated tables are mentioned
    // but the file is queryable.
    generatedTables: isQueryable ? [cf.nodeId] : [],
    isIncludable,
    isQueryable,
    isSearchable,
    isInProjectContext: false, // For now, content nodes can only be from the conversation, not the project. To be revisited if/when we allow connected data in the projects.
    hidden: false, // For now, content nodes are not hidden from the user.
    creator,
  };

  return {
    ...baseAttachment,
    nodeDataSourceViewId: cf.nodeDataSourceViewId,
    contentFragmentId: cf.contentFragmentId,
    nodeId: cf.nodeId,
    nodeType: cf.nodeType,
    sourceUrl: cf.sourceUrl,
  };
}

function shouldSuppressTabularAttachmentHints({
  contentType,
  isInProjectContext,
  skipFileProcessing,
}: {
  contentType: SupportedContentFragmentType;
  isInProjectContext: boolean | null;
  skipFileProcessing: boolean;
}): boolean {
  return (
    isInProjectContext !== true &&
    skipFileProcessing &&
    isSupportedDelimitedTextContentType(contentType)
  );
}

export function getAttachmentFromFileContentFragment(
  cf: FileContentFragmentType
): FileAttachmentType | null {
  const fileId = cf.fileId;
  if (!fileId) {
    logger.warn(
      {
        contentFragmentId: cf.sId,
        contentFragmentCreatedAt: new Date(cf.created),
      },
      "File attachment without a fileId (unsupported legacy)."
    );
    return null;
  }

  // Here, snippet not null is actually to detect file attachments that are prior to the JIT
  // actions, and differentiate them from the newer file attachments that do have a snippet.
  // Former ones cannot be used in JIT.
  const canDoJIT = cf.snippet !== null;
  const isInProjectContext = cf.isInProjectContext === true;
  const shouldSuppressTabularHints = shouldSuppressTabularAttachmentHints({
    contentType: cf.contentType,
    isInProjectContext,
    skipFileProcessing: cf.skipFileProcessing === true,
  });
  const isQueryable =
    !shouldSuppressTabularHints &&
    canDoJIT &&
    isQueryableContentType(cf.contentType);
  const isIncludable =
    !shouldSuppressTabularHints &&
    isConversationIncludableFileContentType(cf.contentType);
  const isSearchable =
    canDoJIT &&
    isSearchableContentType(cf.contentType) &&
    cf.skipDataSourceIndexing !== true;
  const creator: AttachmentCreator | null = cf.context.fullName
    ? {
        type: "user",
        name: cf.context.fullName,
        pictureUrl: cf.context.profilePictureUrl ?? "",
      }
    : null;

  const baseAttachment: BaseConversationAttachmentType = {
    title: cf.title,
    contentType: cf.contentType,
    snippet: cf.snippet,
    contentFragmentVersion: cf.contentFragmentVersion,
    // Backward compatibility: we fallback to the fileId if no generated tables are mentioned
    // but the file is queryable.
    generatedTables:
      cf.generatedTables.length > 0
        ? cf.generatedTables
        : isQueryable
          ? [fileId]
          : [],
    isIncludable,
    isQueryable,
    isSearchable,
    isInProjectContext,
    hidden: cf.hidden,
    creator,
  };

  return {
    ...baseAttachment,
    fileId,
    path: cf.path ?? null,
    source: "user",
    createdAt: cf.created,
  };
}

export function makeFileAttachment({
  fileId,
  source,
  createdAt,
  updatedAt,
  contentType,
  title,
  snippet,
  isInProjectContext,
  hideFromUser,
  skipDataSourceIndexing = false,
  path = null,
  creator = null,
}: {
  fileId: string;
  source: "agent" | "user" | null;
  createdAt?: number;
  updatedAt?: number;
  contentType: AllSupportedFileContentType;
  title: string;
  snippet: string | null;
  isInProjectContext: boolean;
  hideFromUser: boolean;
  skipDataSourceIndexing?: boolean;
  path?: string | null;
  creator?: AttachmentCreator | null;
}): FileAttachmentType {
  const canDoJIT = snippet !== null;
  const isIncludable = isConversationIncludableFileContentType(contentType);
  const isQueryable = canDoJIT && isQueryableContentType(contentType);
  // Files offloaded to disk because their tool output was too large are never indexed in Qdrant,
  // so they must not be advertised as searchable to the model.
  const isSearchable =
    canDoJIT && isSearchableContentType(contentType) && !skipDataSourceIndexing;

  return {
    fileId,
    path,
    source,
    createdAt,
    updatedAt,
    contentType,
    title,
    snippet,
    // For simplicity later, we always set the generatedTables to the fileId if the file is queryable for agent generated files.
    generatedTables: isQueryable ? [fileId] : [],
    contentFragmentVersion: "latest",
    isIncludable,
    isQueryable,
    isSearchable,
    isInProjectContext,
    hidden: hideFromUser,
    creator,
  };
}

export function renderLargePasteXml({
  largePaste,
  content,
  truncated = false,
  path,
}: {
  largePaste: LargePasteType;
  content: string;
  truncated?: boolean;
  path?: string;
}): string {
  const attrs = [`name="${largePaste.title}"`];
  if (truncated) {
    attrs.push('truncated="true"');
  }
  if (path) {
    attrs.push(`path="${path}"`);
  }
  return `<pastedContent ${attrs.join(" ")}>${content}</pastedContent>`;
}

export function renderAttachmentXml({
  attachment,
  content = null,
  hideFlagsAndVersion = false,
}: {
  attachment: ConversationAttachmentType;
  content?: string | null;
  hideFlagsAndVersion?: boolean;
}): string {
  const params = [
    `id="${conversationAttachmentId(attachment)}"`,
    `type="${attachment.contentType}"`,
    `title="${attachment.title}"`,
    ...(hideFlagsAndVersion
      ? []
      : [
          `version="${attachment.contentFragmentVersion}"`,
          `isInProjectContext="${attachment.isInProjectContext}"`,
          `isIncludable="${attachment.isIncludable}"`,
          `isQueryable="${attachment.isQueryable}"`,
          `isSearchable="${attachment.isSearchable}"`,
        ]),
  ];

  if (isContentNodeAttachmentType(attachment)) {
    params.push(`nodeId="${attachment.nodeId}"`);
    if (attachment.sourceUrl) {
      params.push(`sourceUrl="${attachment.sourceUrl}"`);
    }
  }

  let tag = `<attachment ${params.join(" ")}`;

  const contentToRender = content ?? attachment.snippet;

  if (contentToRender) {
    tag += `>${contentToRender}\n</attachment>`;
  } else {
    tag += "/>";
  }

  return tag;
}
